/**
 * The canonical tool registry (RVW1 step 7).
 *
 * ⚠ ONE LIST, NOT THREE. The Défi board, the Calcul station and the
 * Examination Chamber each used to name their own entries; /tools, the desktop
 * menu and the lesson pages now all read this file. A tool that exists in the
 * product and not here is unreachable from /tools — which is the point: adding
 * one is a line, duplicating a list is a bug.
 *
 * Identifiers are English, labels are French (locked rule).
 */
import { EXAM_BASE_PATH } from '../examens/examChamberConstants';
import { TILE_HREF } from '../defi/tileConfig';

// ⚠ ONLY TOOLS WITH A REAL DESTINATION BELONG HERE. Défi Express and the
// level test open inside a running séance, not from a route, and the Scratch
// workshop is out until its rebuild lands — a card that leads back to the
// home page is worse than no card.

export const TOOL_GROUP = Object.freeze({
  PEOPLE: 'people',
  PLANNING: 'planning',
  FINANCE: 'finance',
  INSIGHTS: 'insights',
  PRACTICE: 'practice',
  ASSESSMENT: 'assessment',
  AUTHORING: 'authoring',
});

export const TOOL_GROUPS = Object.freeze([
  { id: TOOL_GROUP.PEOPLE, label: 'Élèves et familles', hint: 'Retrouver les personnes que vous accompagnez.' },
    { id: TOOL_GROUP.PLANNING, label: "Missions", hint: "Toutes les missions dans un seul endroit." },
  { id: TOOL_GROUP.FINANCE, label: 'Facturation', hint: 'Factures et paiements au même endroit.' },
  { id: TOOL_GROUP.INSIGHTS, label: 'Suivi', hint: 'Voir votre activité en un coup d’œil.' },
  { id: TOOL_GROUP.PRACTICE, label: 'S’entraîner', hint: 'Des activités prêtes à lancer.' },
  { id: TOOL_GROUP.ASSESSMENT, label: 'Évaluer', hint: 'Examens et sujets officiels.' },
  { id: TOOL_GROUP.AUTHORING, label: 'Créer', hint: 'Concevoir et ajuster vos contenus.' },
]);

/**
 * `featured` controls the public /tools preview; `private` hides teacher routes there.
 * `lessons` are the lesson slugs that explain the notion the tool drills.
 */
export const TOOLS = Object.freeze([
  { id: 'teacher-portfolio', label: 'Ma page de prof', hint: 'Votre site de professeur, prêt en quelques minutes.', href: '/portfolio', view: 'portfolio', group: TOOL_GROUP.AUTHORING, private: true, lessons: [], aliases: ['portfolio', 'ma page', 'site de prof', 'site web'] },
  { id: 'students-list', label: 'Liste des élèves', hint: 'Fiches et suivi de vos élèves.', href: '/students', group: TOOL_GROUP.PEOPLE, private: true, shortcut: true, lessons: [], aliases: ['élèves', 'liste élèves', 'mes élèves', 'students_list'] },
  { id: 'families-list', label: 'Liste des familles', hint: 'Coordonnées et fiches familles.', href: '/families', group: TOOL_GROUP.PEOPLE, private: true, shortcut: true, lessons: [], aliases: ['familles', 'liste familles', 'mes familles', 'family_list'] },
  { id: 'schedule', label: 'Planning des cours', hint: 'Consulter et organiser vos cours.', href: '/schedules', view: 'schedules', group: TOOL_GROUP.PLANNING, private: true, shortcut: true, mobileShortcut: true, lessons: [], aliases: ['planning', 'agenda', 'planifier un cours'] },
  { id: 'missions-library', label: 'Missions', hint: 'Chercher et filtrer les missions de tous vos élèves.', href: '/missions', group: TOOL_GROUP.PLANNING, private: true, lessons: [], aliases: ['missions', 'devoirs', 'exercices donnés', 'chercher une mission'] },
  { id: 'new-course', label: 'Démarrer un cours', hint: 'Ouvrir une nouvelle séance.', href: '/course', group: TOOL_GROUP.PLANNING, private: true, shortcut: true, lessons: [], aliases: ['nouveau cours', 'commencer un cours'] },
  {
    id: 'calcul-station',
    label: 'Station de calcul',
    hint: 'Les quatre opérations, par niveau, avec le tableau de bord.',
    href: '/calcul',
    group: TOOL_GROUP.PRACTICE,
    featured: true,
    lessons: ['poser-une-addition', 'tables-de-multiplication'],
    aliases: ['calcul mental', 'exercices de calcul'],
  },
  {
    id: 'number-words',
    label: 'Nombres en lettres',
    hint: 'Écrire un nombre en lettres, ou le lire, jusqu’en 6e.',
    href: TILE_HREF['nombres-en-lettres'],
    group: TOOL_GROUP.PRACTICE,
    featured: true,
    lessons: ['ecrire-les-nombres-en-lettres'],
    aliases: ['écrire les nombres en lettres', 'nombres lettres'],
  },
  {
    id: 'national-exam-simulator',
    label: 'Sujets officiels',
    hint: 'Brevet et bac en conditions réelles, chronométrés et corrigés.',
    href: EXAM_BASE_PATH,
    group: TOOL_GROUP.ASSESSMENT,
    featured: true,
    lessons: [],
    aliases: ['simulateur examen', 'annales brevet', 'annales bac'],
  },
  {
    id: 'exam-builder',
    label: 'Créer un examen',
    hint: 'Un contrôle ou un sujet officiel, envoyé aux élèves.',
    href: '/examens/nouveau',
    group: TOOL_GROUP.ASSESSMENT,
    featured: true,
    lessons: [],
    aliases: ['nouvel examen', 'créer un contrôle'],
  },
  {
    id: 'exam-monitor',
    label: 'Suivi des examens',
    hint: 'Surveiller les examens attribués et partager les liens personnels des élèves.',
    href: '/examens/suivi',
    group: TOOL_GROUP.ASSESSMENT,
    private: true,
    featured: false,
    lessons: [],
    aliases: ['surveillance examen', 'monitoring examen', 'suivi contrôle'],
  },
  {
    id: 'model-editor',
    label: 'Modèles',
    hint: 'Relire et corriger les modèles de questions.',
    href: '/models',
    group: TOOL_GROUP.AUTHORING,
    featured: false,
    lessons: [],
    aliases: ['modèles de questions', 'éditeur de modèles'],
  },
  { id: 'invoices', label: 'Factures', hint: 'Créer et retrouver vos factures.', href: '/invoices', view: 'generateInvoice', group: TOOL_GROUP.FINANCE, private: true, featured: false, lessons: [], shortcut: true, mobileShortcut: true, aliases: ['facture', 'facturation'] },
  { id: 'payments', label: 'Paiements', hint: 'Enregistrer et consulter les règlements.', href: '/payments', view: 'payments', group: TOOL_GROUP.FINANCE, private: true, featured: false, lessons: [], shortcut: true, mobileShortcut: true, aliases: ['paiement', 'règlements'] },
  { id: 'statistics', label: 'Statistiques', hint: 'Revenus, élèves et activité.', href: '/statistics', view: 'statistics', group: TOOL_GROUP.INSIGHTS, private: true, featured: false, lessons: [], shortcut: true, mobileShortcut: true, aliases: ['stats', 'bilan'] },
]);

export const featuredTools = () => TOOLS.filter((tool) => tool.featured);
export const otherTools = () => TOOLS.filter((tool) => !tool.featured && !tool.private);
/** NEW1: a shortcut opens a page already reachable from the nav or the « + » menu; the catalogue lists it apart. */
export const shortcutTools = () => TOOLS.filter((tool) => tool.shortcut);
export const realTools = (isAuthenticated) => catalogueTools(isAuthenticated).filter((tool) => !tool.shortcut);
export const mobileShortcutTools = () => TOOLS.filter((tool) => tool.mobileShortcut);
export const catalogueTools = (isAuthenticated) => TOOLS.filter((tool) => isAuthenticated || !tool.private);
export const toolById = (id) => TOOLS.find((tool) => tool.id === id) || null;
const normaliseToolText = (value) => String(value || '').toLowerCase()
  .normalize('NFD').replace(/[\u0300-\u036f]/g, '')
  .replace(/[^a-z0-9]+/g, ' ').trim();

export const searchCatalogueTools = (query, isAuthenticated = true) => {
  const wanted = normaliseToolText(query);
  if (wanted.length < 2) return [];
  const padded = ` ${wanted} `;
  return catalogueTools(isAuthenticated)
    .map((tool) => {
      const terms = [tool.label, tool.id, ...(tool.aliases || [])].map(normaliseToolText);
      const exact = terms.some((term) => term === wanted);
      const included = terms.some((term) => term.length > 2 && padded.includes(` ${term} `));
      const partial = terms.some((term) => term.startsWith(wanted) && wanted.length >= 3);
      return { tool, confidence: exact ? 0.99 : included ? 0.88 : partial ? 0.76 : 0 };
    })
    .filter((result) => result.confidence > 0)
    .sort((a, b) => b.confidence - a.confidence)
    .slice(0, 4);
};
export const toolsForLesson = (slug) =>
  TOOLS.filter((tool) => !tool.shortcut && tool.lessons.includes(slug));

export const TOOLS_PATH = '/tools';

export const TOOLS_COPY = Object.freeze({
  TITLE: 'Outils',
  SUBTITLE: 'Tout ce qui se lance en un clic pendant une séance.',
  CATALOGUE_TITLE: 'Tous vos outils',
  CATALOGUE_SUBTITLE: 'Retrouvez rapidement ce qu’il vous faut pour votre activité.',
  SEARCH_HINT: 'Chercher un outil ou décrire une action…',
  BROWSE_ALL: 'Autres',
  MORE: 'Plus d’outils',
  OPEN: 'Ouvrir',
  PRIVATE_SECTION: 'Outils de gestion',
  SHORTCUTS: 'Raccourcis',
  RELATED_LESSONS: 'Leçons liées',
});
